import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, LayersIcon, ChevronDownIcon } from 'lucide-react';
import { AllAmericanGutterQuoteForm } from './AllAmericanGutterQuoteForm';
interface AllAmericanGutterLandingPageProps {
  initialProject?: string;
  onClose: () => void;
}
export function AllAmericanGutterLandingPage({
  initialProject,
  onClose
}: AllAmericanGutterLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>(initialProject || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialProject) {
      setSelectedProject(initialProject);
    }
  }, [initialProject]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedProject) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <AllAmericanGutterQuoteForm initialZip={zipCode} projectType={selectedProject} onClose={onClose} data-id="element-205" />;
  }
  const projects = [{
    id: 'gutters',
    name: 'New Gutters',
    icon: <LayersIcon className="w-6 h-6" data-id="element-206" />,
    desc: 'Complete gutter replacement'
  }, {
    id: 'protection',
    name: 'Gutter Protection',
    icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-207" />,
    desc: 'Keep leaves and debris out'
  }, {
    id: 'both',
    name: 'Both (Gutters + Protection)',
    icon: <StarIcon className="w-6 h-6" data-id="element-208" />,
    desc: 'Ultimate gutter solution'
  }];
  const projectLabel = projects.find(p => p.id === selectedProject)?.name || '';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-209">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-between items-center shadow-sm relative z-20" data-id="element-210">
        <div className="flex flex-col" data-id="element-211">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-212">
            Partner
          </span>
          <div className="flex items-baseline gap-2" data-id="element-213">
            <span className="text-2xl md:text-3xl font-black text-[#4B7B3F] tracking-tight" data-id="element-214">
              All American GP
            </span>
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-215">
            Gutter Guards &bull; Gutter Protection &bull; New Gutters
          </span>
        </div>
        <div className="hidden md:flex flex-col items-end" data-id="element-216">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-217">
            Call Us:
          </span>
          <span className="text-lg font-bold text-[#4B7B3F]" data-id="element-218">888-323-8090</span>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#4B7B3F] py-4 md:py-6 border-t border-[#3a5e30]" data-id="element-219">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-220">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-221">
            Celebrating 10 Years of Excellence
          </h2>
          <p className="text-green-100 text-sm mt-1" data-id="element-222">
            The Best Gutter Protection For Your Home.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-223">
        <div className="absolute inset-0 z-0" data-id="element-224">
          <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80" alt="Home exterior with gutters" className="w-full h-full object-cover" data-id="element-225" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-226" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-227">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-228">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-229">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-230">
                End Gutter Cleaning. Forever.
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-231">
                Get a free estimate from America's trusted gutter protection
                experts.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-232">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-233">
                <h2 className="text-xl font-bold text-[#4B7B3F] text-center mb-6" data-id="element-234">
                  {initialProject ? 'Enter Your Zip Code To Start' : 'Select Project & Enter Your Zip'}
                </h2>

                {/* Project dropdown */}
                {!initialProject && <div className="mb-6 relative" ref={dropdownRef} data-id="element-235">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-green-50 ${selectedProject ? 'border-[#4B7B3F]' : 'border-green-200'} ${dropdownOpen ? 'border-[#4B7B3F] shadow-md' : 'hover:border-green-300'}`} data-id="element-236">
                      <div className="flex items-center gap-3" data-id="element-237">
                        {selectedProject ? <>
                            <span className="text-[#4B7B3F]" data-id="element-238">
                              {projects.find(p => p.id === selectedProject)?.icon}
                            </span>
                            <span className="font-bold text-[#4B7B3F] text-lg" data-id="element-239">
                              {projects.find(p => p.id === selectedProject)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-240">
                            Choose project type...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#4B7B3F] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-241" />
                    </button>

                    <AnimatePresence data-id="element-242">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-green-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-243">
                          {projects.map(proj => <button key={proj.id} onClick={() => {
                      setSelectedProject(proj.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedProject === proj.id ? 'bg-green-50 text-[#4B7B3F]' : 'bg-white text-gray-700 hover:bg-green-50 hover:text-[#4B7B3F]'} border-b border-green-100 last:border-b-0`} data-id="element-244">
                              <span className={`${selectedProject === proj.id ? 'text-[#4B7B3F]' : 'text-gray-400'}`} data-id="element-245">
                                {proj.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-246">
                                {proj.name}
                              </span>
                              {selectedProject === proj.id && <CheckCircle2Icon className="w-5 h-5 text-[#4B7B3F] ml-auto" data-id="element-247" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected project badge when pre-selected */}
                {initialProject && <div className="flex items-center justify-center gap-2 mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-248">
                    {projects.find(p => p.id === initialProject)?.icon}
                    <span className="font-bold text-[#4B7B3F]" data-id="element-249">
                      {projectLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-250">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#4B7B3F] focus:ring-1 focus:ring-[#4B7B3F] transition-colors" maxLength={5} data-id="element-251" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-252">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-253" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedProject} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedProject ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-254">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-255">
                  <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200" data-id="element-256">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-257" />
                    <span className="text-xs font-bold text-green-800 uppercase tracking-tight" data-id="element-258">
                      Lifetime Warranty
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-259">
                    <div className="font-black text-blue-700 text-sm" data-id="element-260">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-261">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Accent Bar */}
      <div className="w-full h-3 bg-[#4B7B3F]" data-id="element-262" />

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-263">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-264">
          <div className="text-center mb-16" data-id="element-265">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-266">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-267">
            {[{
            title: 'Choose Your Project',
            desc: 'Select from new gutters, gutter protection, or both. Our experts specialize in keeping your home safe from water damage.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Get Your Free Estimate',
            desc: 'An All American representative will visit your home, assess your gutters, review options, and provide a no-obligation quote.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Professional Installation',
            desc: 'Our trained installers handle everything from start to finish, backed by our lifetime clog-free guarantee.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-268">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-269">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-270" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-271">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-272">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#FAFAFA]" data-id="element-273">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-274">
          <div className="text-center mb-12" data-id="element-275">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-276">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-277">
            {[{
            name: 'Sarah T.',
            location: 'Columbus, OH',
            text: "All American installed gutter guards on my home last fall. For the first time in years, I didn't have to climb a ladder to clean out leaves. Best investment ever!"
          }, {
            name: 'David & Lisa M.',
            location: 'Atlanta, GA',
            text: 'The crew was professional, fast, and cleaned up everything before leaving. The new gutters look great and handle heavy rain perfectly.'
          }, {
            name: 'Robert C.',
            location: 'Charlotte, NC',
            text: 'I got quotes from three different companies. All American had the best product and a very fair price. The lifetime warranty gives me total peace of mind.'
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-278">
                <div className="flex text-yellow-400 mb-4" data-id="element-279">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-280" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-281">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div data-id="element-282">
                  <p className="font-bold text-gray-900" data-id="element-283">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-284">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4B7B3F] py-16" data-id="element-285">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-286">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-287">
            <div className="flex flex-col gap-8" data-id="element-288">
              <div className="flex flex-col" data-id="element-289">
                <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider mb-[-4px]" data-id="element-290">
                  Partner
                </span>
                <span className="text-3xl font-black text-white tracking-tight" data-id="element-291">
                  All American GP
                </span>
                <span className="text-[10px] font-semibold text-white/90" data-id="element-292">
                  Gutter Guards &bull; Gutter Protection &bull; New Gutters
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-293">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-294">
              <div className="flex flex-col gap-4" data-id="element-295">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-296">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-297">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-298">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-299">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-300">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-301">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-302">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-303">
              All American Gutter Protection services are available nationwide.
              All estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}